import DOMPurify from 'dompurify'

export const CONTENT_SCREEN_VERSION = 1

const ALLOWED_TAGS = [
  'p',
  'br',
  'strong',
  'b',
  'em',
  'i',
  'u',
  'ul',
  'ol',
  'li',
  'h3',
  'a',
]
// data-list is how the rich-text editor marks bullet items inside an <ol>.
const ALLOWED_ATTR = ['href', 'target', 'rel', 'data-list']
const SAFE_LINK = /^(?:https?:|mailto:)/i
const TITLE_MAX_LENGTH = 80

let purifier = null

// Own DOMPurify instance, so these hooks do not change sanitising elsewhere in the app.
const getPurifier = () => {
  if (!purifier) {
    purifier = DOMPurify(window)
    purifier.addHook('uponSanitizeAttribute', (node, data) => {
      if (data.attrName === 'href' && !SAFE_LINK.test(data.attrValue)) {
        data.keepAttr = false
      }
    })
    purifier.addHook('afterSanitizeAttributes', node => {
      if (node.tagName === 'A') {
        node.setAttribute('target', '_blank')
        node.setAttribute('rel', 'noopener noreferrer')
      }
    })
  }
  return purifier
}

export const sanitizeContentHtml = html =>
  getPurifier().sanitize(html || '', {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    ALLOW_DATA_ATTR: false,
  })

const blockTexts = html => {
  const doc = new DOMParser().parseFromString(
    sanitizeContentHtml(html),
    'text/html'
  )
  const blocks = [...doc.body.querySelectorAll('p, h3, li')].map(el =>
    el.textContent.trim()
  )
  return blocks.length > 0 ? blocks : [doc.body.textContent.trim()]
}

export const contentBodyText = html =>
  blockTexts(html).filter(Boolean).join('\n')

export const emptyContentScreen = () => ({
  version: CONTENT_SCREEN_VERSION,
  headline: '',
  subheading: '',
  bodyHtml: '',
  image: null,
  imagePosition: 0,
})

export const validateContentScreen = content => {
  const errors = []
  const hasHeadline = !!content?.headline?.trim()
  const hasBody = contentBodyText(content?.bodyHtml) !== ''
  const hasImage = !!content?.image?.key

  if (!hasHeadline && !hasBody && !hasImage) {
    errors.push({
      code: 'EMPTY',
      message: 'Add a headline, some text or an image.',
    })
  }
  if (hasImage && !content.image.altText?.trim()) {
    errors.push({
      code: 'IMAGE_ALT_REQUIRED',
      message: 'Add alt text that describes the image.',
    })
  }
  return errors
}

export const normalizeContentScreen = content => ({
  version: CONTENT_SCREEN_VERSION,
  headline: content?.headline?.trim() || '',
  subheading: content?.subheading?.trim() || '',
  bodyHtml: contentBodyText(content?.bodyHtml)
    ? sanitizeContentHtml(content.bodyHtml)
    : '',
  image: content?.image?.key
    ? {
        key: content.image.key,
        altText: content.image.altText?.trim() || '',
        caption: content.image.caption?.trim() || '',
      }
    : null,
  imagePosition: content?.imagePosition === 1 ? 1 : 0,
})

export const contentScreenTitle = content => {
  const title =
    content?.headline?.trim() ||
    blockTexts(content?.bodyHtml).find(Boolean) ||
    content?.image?.altText?.trim() ||
    'Untitled content screen'
  return title.length > TITLE_MAX_LENGTH
    ? `${title.slice(0, TITLE_MAX_LENGTH - 1)}…`
    : title
}
